import React from "react";
import {
  AlertCircle,
  AlignJustify,
  ArrowDown,
  ArrowDownUp,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowRightFromLine,
  ArrowRightSquare,
  ArrowUp,
  Bold,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  CircleAlert,
  CircleHelp,
  CirclePercent,
  CircleUserRound,
  ClipboardCheck,
  ClipboardPen,
  Clock,
  Component,
  Construction,
  Copy,
  CornerDownRight,
  DollarSign,
  Download,
  Earth,
  Ellipsis,
  EllipsisVertical,
  Expand,
  Eye,
  EyeOff,
  File,
  FileImage,
  FileText,
  FileVideo,
  Filter,
  FolderCheck,
  FolderClosed,
  FolderDown,
  FolderUp,
  GanttChart,
  Glasses,
  Globe,
  GripHorizontal,
  GripVertical,
  Group,
  HandCoins,
  Hash,
  HelpCircle,
  History,
  Home,
  Image,
  IndentDecrease,
  IndentIncrease,
  Info,
  Italic,
  LandPlot,
  Languages,
  Layers,
  Library,
  Link,
  List,
  ListOrdered,
  Loader2,
  LocateFixed,
  LogOut,
  LucideProps,
  Mail,
  Mailbox,
  Map,
  MapPin,
  MapPinned,
  MessageCircleWarning,
  Minus,
  MoreHorizontal,
  MousePointerClick,
  PanelsTopLeft,
  Paperclip,
  Pencil,
  PencilRuler,
  PenTool,
  Pin,
  Plus,
  Printer,
  Redo2,
  Ruler,
  Search,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  SquareArrowUpRight,
  SquareCheckBig,
  TableProperties,
  Tags,
  Ticket,
  Trash,
  Trash2,
  TriangleAlert,
  Underline,
  Undo2,
  Upload,
  Users,
  UserSearch,
  UsersRound,
  Waves,
  X,
  XCircle,
} from "lucide-react";
import { colors } from "../../../stitches.config";

export const Icons = {
  "alert-circle": AlertCircle,
  "align-justify": AlignJustify,
  "arrow-down-up": ArrowDownUp,
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  "arrow-left-right": ArrowLeftRight,
  "arrow-right-square": ArrowRightSquare,
  "arrow-right-from-line": ArrowRightFromLine,
  "arrow-up": ArrowUp,
  "arrow-down": ArrowDown,
  bold: Bold,
  calendar: Calendar,
  "chart-gantt": GanttChart,
  check: Check,
  "check-circle": CheckCircle2,
  "chevron-down": ChevronDown,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "chevron-up": ChevronUp,
  circle: Circle,
  "circle-alert": CircleAlert,
  "circle-help": CircleHelp,
  "circle-percent": CirclePercent,
  "circle-user-round": CircleUserRound,
  "clipboard-check": ClipboardCheck,
  "clipboard-pen": ClipboardPen,
  clock: Clock,
  construction: Construction,
  component: Component,
  copy: Copy,
  "corner-down-right": CornerDownRight,
  download: Download,
  "dollar-sign": DollarSign,
  earth: Earth,
  ellipsis: Ellipsis,
  "ellipsis-vertical": EllipsisVertical,
  "eye-off": EyeOff,
  eye: Eye,
  expand: Expand,
  file: File,
  "file-text": FileText,
  filter: Filter,
  "folder-check": FolderCheck,
  "folder-closed": FolderClosed,
  "folder-down": FolderDown,
  "folder-up": FolderUp,
  glasses: Glasses,
  globe: Globe,
  "grip-horizontal": GripHorizontal,
  "grip-vertical": GripVertical,
  group: Group,
  hash: Hash,
  "hand-coins": HandCoins,
  "help-circle": HelpCircle, //deprecated same as circle-help
  history: History,
  home: Home, //this is the "house" icon
  image: Image,
  "indent-decrease": IndentDecrease,
  "indent-increase": IndentIncrease,
  info: Info,
  italic: Italic,
  "land-plot": LandPlot,
  languages: Languages,
  layers: Layers,
  library: Library,
  link: Link,
  list: List,
  "list-ordered": ListOrdered,
  loading: Loader2,
  "locate-fixed": LocateFixed,
  logout: LogOut,
  mail: Mail,
  mailbox: Mailbox,
  map: Map,
  "map-pin": MapPin,
  "map-pinned": MapPinned,
  "message-circle-warning": MessageCircleWarning,
  minus: Minus,
  "more-horizontal": MoreHorizontal, //deprecated: same as ellipsis
  "panels-top-left": PanelsTopLeft,
  paperclip: Paperclip,
  pencil: Pencil,
  "pencil-ruler": PencilRuler,
  "pen-tool": PenTool,
  pin: Pin,
  plus: Plus,
  printer: Printer,
  redo: Redo2,
  ruler: Ruler,
  search: Search,
  settings: Settings,
  "shield-alert": ShieldAlert,
  "shield-check": ShieldCheck,
  sparkles: Sparkles,
  "square-arrow-up-right": SquareArrowUpRight,
  "square-check-big": SquareCheckBig,
  "table-properties": TableProperties,
  tags: Tags,
  ticket: Ticket,
  trash: Trash, //deprecated
  "trash-2": Trash2,
  "triangle-alert": TriangleAlert,
  underline: Underline,
  undo: Undo2,
  upload: Upload,
  "user-search": UserSearch,
  users: Users, //deprecated
  "users-round": UsersRound,
  waves: Waves,
  x: X,
  "x-circle": XCircle,
  "file-image": FileImage,
  "file-video": FileVideo,
  "mouse-pointer-click": MousePointerClick,
};

export type IconNames = keyof typeof Icons;

export const iconNames = Object.keys(Icons);

export interface IconProps extends Partial<LucideProps> {
  color: keyof typeof colors;
  iconName: keyof typeof Icons;
  testId?: string;
}

export const Icon = ({
  iconName,
  color,
  size,
  strokeWidth,
  cursor,
  onClick,
  testId,
  ...props
}: IconProps) => {
  const Icon = Icons[iconName];

  const stitchesColor = colors[color].value;

  return (
    <Icon
      color={stitchesColor}
      size={size}
      strokeWidth={strokeWidth}
      cursor={cursor}
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "inherit" }}
      data-testid={testId}
      {...props}
    />
  );
};

export const IconButton = ({
  iconName,
  onClick,
  style,
  testId,
}: Omit<IconProps, "color">) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <Icon
      testId={testId}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      iconName={iconName}
      color={hovered ? "contentPrimary" : "contentSecondary"}
      size={24}
      onClick={onClick}
      style={{ ...style, flexShrink: 0, cursor: "pointer" }}
    />
  );
};
